import React from "react";
import Link from "../audience/ScientificLink";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  Gauge,
  Target,
  Users,
  Wrench,
} from "lucide-react";

const outcomeTargets = [
  {
    icon: Clock,
    title: "Hands-on time",
    description:
      "Measure interventions or hands-on minutes per batch in your current SOP. Agree which steps to automate and which should remain deliberate checkpoints.",
  },
  {
    icon: Target,
    title: "Transfer performance",
    description:
      "Your method owner approves criteria such as precision, recovery, carryover, or control behavior before implementation begins.",
  },
  {
    icon: ClipboardCheck,
    title: "Run traceability",
    description:
      "Keep versioned method files and execution records, including inputs, plate maps, instrument events, and deviations.",
  },
  {
    icon: Wrench,
    title: "Maintainability",
    description:
      "Document how to update, review, troubleshoot, and recover methods when equipment, labware, reagents, or operating conditions change.",
  },
  {
    icon: Gauge,
    title: "Capacity and scheduling",
    description:
      "Model realistic batch size, deck constraints, runtime, and operator availability on the instruments named in the engagement.",
  },
  {
    icon: Users,
    title: "Team handoff",
    description:
      "Name who will run, review, and support the workflow. Prepare an operator walkthrough and the training and documentation each role needs.",
  },
];

const processSteps = [
  {
    step: "01",
    title: "Baseline the workflow",
    description:
      "Review the SOP, equipment, labware, controls, operator steps, and known failure modes.",
  },
  {
    step: "02",
    title: "Write the acceptance plan",
    description:
      "Choose the measures, test conditions, pass criteria, and evidence required for handoff.",
  },
  {
    step: "03",
    title: "Build and test",
    description:
      "Implement the workflow, run the agreed test cases, and document deviations and decisions.",
  },
  {
    step: "04",
    title: "Review and hand off",
    description:
      "Compare results with the plan, resolve open items, and transfer the method to its operating team.",
  },
];

const acceptanceExamples = [
  "Operator interventions and hands-on minutes per batch",
  "Assay-specific performance criteria selected by the method owner",
  "Failed-run detection and recovery expectations",
  "Required source records, execution evidence, and change history",
  "Data mappings, reconciliation totals, exceptions, and reviewer decisions",
  "Agent permissions, human-review gates, and safe-stop behavior",
  "Training, ownership, and maintenance responsibilities",
];

const fadeIn = (delay = 0, reduced = false) =>
  reduced
    ? {}
    : {
        initial: { opacity: 0, y: 20 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-10% 0px" },
        transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1], delay },
      };

const Benefits = () => {
  const reduced = useReducedMotion();

  return (
    <main className="blue-handoff-canvas px-6 pb-20">
      {/* Header */}
      <section className="noise relative -mx-6 mb-24 overflow-hidden border-b border-gray-200 px-6 pb-20 pt-28 dark:border-white/[0.08] sm:pb-24 sm:pt-32">
        <div className="hero-mesh pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="wide-shell-lg relative z-10 mx-auto max-w-6xl">
          <div className="grid items-end gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:gap-20">
            <motion.h1
              {...fadeIn(0.05, reduced)}
              className="max-w-4xl font-serif text-5xl font-normal leading-[0.98] tracking-[-0.035em] [text-wrap:pretty] sm:text-6xl lg:text-7xl min-[1920px]:text-[5rem]"
            >
              How will we know <span className="blue-handoff-display">it works?</span>
            </motion.h1>

            <div>
              <motion.p
                {...fadeIn(0.1, reduced)}
                className="mb-7 text-base leading-7 text-gray-600 dark:text-white/65 sm:text-lg sm:leading-8"
              >
                Your method owner chooses what success means for this workflow.
                We write down the conditions, run the agreed cases, and hand
                over the results with the method.
              </motion.p>

              <motion.div
                {...fadeIn(0.15, reduced)}
                className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center"
              >
                <Link
                  to="/contact?solution=workflow-assessment&source=outcomes-hero"
                  data-cta="define-acceptance-targets"
                  data-solution-id="workflow-assessment"
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:bg-white dark:text-[#050507] dark:hover:bg-white/90 dark:focus-visible:ring-offset-[#050507]"
                >
                  Tell us what must pass
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/contact?solution=workflow-review&source=outcomes-hero"
                  data-cta="workflow-review"
                  data-solution-id="workflow-review"
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 transition-colors duration-200 hover:border-gray-400 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:border-white/[0.16] dark:text-white/70 dark:hover:border-white/[0.28] dark:hover:text-white dark:focus-visible:ring-offset-[#050507]"
                >
                  Review a workflow
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement-specific targets */}
      <section className="wide-shell-md max-w-5xl mx-auto mb-28" aria-labelledby="outcome-targets-heading">
        <motion.div {...fadeIn(0, reduced)} className="max-w-2xl mb-12">
          <h2
            id="outcome-targets-heading"
            className="text-3xl md:text-4xl font-bold tracking-tight mb-4"
          >
            What matters for your workflow
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5 min-[1920px]:grid-cols-3">
          {outcomeTargets.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.article
                key={item.title}
                {...fadeIn(index * 0.06, reduced)}
                className="flex h-full flex-col rounded-xl border border-gray-200 bg-gray-50 p-6 transition-colors duration-200 hover:border-gray-300 dark:border-white/[0.08] dark:bg-white/[0.03] dark:hover:border-white/[0.16]"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-primary" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 dark:text-white/60 leading-relaxed">
                  {item.description}
                </p>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* Acceptance process */}
      <section className="wide-shell-md max-w-5xl mx-auto mb-28" aria-labelledby="acceptance-process-heading">
        <motion.div {...fadeIn(0, reduced)} className="text-center max-w-2xl mx-auto mb-14">
          <h2
            id="acceptance-process-heading"
            className="text-3xl md:text-4xl font-bold tracking-tight mb-4"
          >
            Agree on done before we start
          </h2>
        </motion.div>

        <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {processSteps.map((item, index) => (
            <motion.li
              key={item.step}
              {...fadeIn(index * 0.08, reduced)}
              className="rounded-xl border border-gray-200 bg-gray-50 p-6 dark:border-white/[0.08] dark:bg-white/[0.03]"
            >
              <span className="font-mono text-xs font-medium text-primary">
                {item.step}
              </span>
              <h3 className="text-base font-semibold mt-4 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-600 dark:text-white/60 leading-relaxed">
                {item.description}
              </p>
            </motion.li>
          ))}
        </ol>
      </section>

      {/* Acceptance plan contents */}
      <motion.section
        {...fadeIn(0, reduced)}
        className="wide-shell-md max-w-5xl mx-auto mb-28 rounded-2xl border border-gray-200 bg-gray-50 p-8 md:p-12 dark:border-white/[0.08] dark:bg-white/[0.03]"
        aria-labelledby="acceptance-plan-heading"
      >
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16 items-start">
          <div>
            <h2
              id="acceptance-plan-heading"
              className="text-3xl md:text-4xl font-bold tracking-tight mb-4"
            >
              What goes in the plan
            </h2>
            <p className="text-gray-600 dark:text-white/60 leading-relaxed">
              We name the assay, instruments, labware, data, and operating team
              in scope, along with what falls outside the work.
            </p>
          </div>

          <ul className="space-y-4">
            {acceptanceExamples.map((example) => (
              <li key={example} className="flex items-start gap-3">
                <CheckCircle2
                  className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span className="text-sm text-gray-700 dark:text-white/70 leading-relaxed">
                  {example}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </motion.section>

      <motion.section
        {...fadeIn(0, reduced)}
        className="wide-shell-md max-w-5xl mx-auto mb-28 grid gap-8 rounded-lg border border-gray-200 bg-gray-950 p-8 text-white dark:border-white/[0.08] dark:bg-[#090b11] md:grid-cols-[0.75fr_1.25fr] md:p-12"
        aria-labelledby="quality-boundary-heading"
      >
        <div>
          <h2 id="quality-boundary-heading" className="text-3xl md:text-4xl font-bold tracking-tight">
            What the test record covers
          </h2>
        </div>
        <div className="space-y-5 text-sm leading-7 text-white/65">
          <p>
            Cornucopia reports against the criteria in the project scope and
            records the conditions, deviations, results, and known limits. Our
            standard engagements are intended for research use.
          </p>
          <p>
            If this work will enter a regulated environment, your Quality Unit
            decides what qualification and validation are required.
          </p>
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        {...fadeIn(0, reduced)}
        className="wide-shell-md max-w-4xl mx-auto text-center"
        aria-labelledby="outcomes-cta-heading"
      >
        <h2
          id="outcomes-cta-heading"
          className="text-3xl md:text-4xl font-bold font-serif tracking-tight mb-8"
        >
          What would your team need to see?
        </h2>
        <Link
          to="/contact?solution=workflow-review&source=outcomes-footer"
          data-cta="scope-engagement"
          data-solution-id="workflow-review"
          className="min-h-[44px] inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:bg-white dark:text-[#050507] dark:hover:bg-white/90 dark:focus-visible:ring-offset-[#050507]"
        >
          Review the workflow
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </motion.section>
    </main>
  );
};

export default Benefits;
